import { useState, useEffect } from 'react';

/**
 * Format a phone number for display: (XXX) XXX-XXXX
 */
const formatPhoneNumber = (value) => {
  if (!value) return '';
  
  // Remove all non-digits
  const phoneNumber = value.replace(/\D/g, '');
  const phoneNumberLength = phoneNumber.length;
  
  if (phoneNumberLength < 4) {
    return phoneNumber;
  }
  
  if (phoneNumberLength < 7) {
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
  }
  
  return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
};

/**
 * Remove formatting from phone number (store as digits only)
 */
const unformatPhoneNumber = (value) => {
  return value.replace(/\D/g, '');
};

const PhoneInput = ({ 
  label = "Phone Number", 
  name = "phone", 
  value = "", 
  onChange, 
  error = "",
  required = false,
  placeholder = "(555) 123-4567"
}) => {
  const [displayValue, setDisplayValue] = useState('');

  // Update display value when prop value changes
  useEffect(() => {
    setDisplayValue(formatPhoneNumber(value));
  }, [value]);

  const handleChange = (e) => {
    const inputValue = e.target.value;
    
    // Format for display
    const formatted = formatPhoneNumber(inputValue);
    setDisplayValue(formatted);
    
    // Send unformatted (digits only) to parent
    const unformatted = unformatPhoneNumber(inputValue);
    onChange({
      target: {
        name: name,
        value: unformatted
      }
    });
  };

  const handleKeyDown = (e) => {
    // Allow: backspace, delete, tab, escape, enter
    if ([8, 9, 27, 13, 46].indexOf(e.keyCode) !== -1 ||
        // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
        (e.keyCode === 65 && e.ctrlKey === true) ||
        (e.keyCode === 67 && e.ctrlKey === true) ||
        (e.keyCode === 86 && e.ctrlKey === true) ||
        (e.keyCode === 88 && e.ctrlKey === true) ||
        // Allow: home, end, left, right
        (e.keyCode >= 35 && e.keyCode <= 39)) {
      return;
    }
    
    // Ensure that it is a number and stop the keypress
    if ((e.shiftKey || (e.keyCode < 48 || e.keyCode > 57)) && (e.keyCode < 96 || e.keyCode > 105)) {
      e.preventDefault();
    }
  };

  const inputClasses = `bfs-input ${error ? 'bfs-input-error' : ''}`;

  return (
    <div className="mb-4">
      {label && (
        <label htmlFor={name} className="bfs-label">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      
      <input
        type="tel"
        id={name}
        name={name}
        value={displayValue}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        maxLength="14"
        className={inputClasses}
        aria-invalid={!!error}
        aria-describedby={error ? `${name}-error` : undefined}
      />
      
      {error && (
        <p id={`${name}-error`} className="bfs-error-text" role="alert">
          {error}
        </p>
      )}
    </div>
  );
};

export default PhoneInput;
